import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactDOM from "react-dom";
import { Search, MoreVertical, Plus, Filter, X, ChevronDown } from "lucide-react";
import PageHeader from "../Ui_Components/PageHeader";
import Pagination from "../Ui_Components/Pagination";
import { Button } from "../Ui_Components/Button";
import { ToastContainer, useToast } from "../Ui_Components/ToastMessage";
import {
  archiveAdhocMealPlan,
  assignAdhocMealPlanToClient,
  deleteAdhocMealPlan,
  getAdhocMealPlanList,
  restoreAdhocMealPlan,
} from "../../services/adhocMealPlans";
import { getClientListForMealPlan, getMealPlanTypeList } from "../../services/mealPlans";
import { getUserProfileDetails } from "../../services/clientDetails";
import IngredientReasonModal, {
  ReasonModalMode,
} from "../Food_Library/Ingredient/IngredientReasonModal";

type TabType = "all" | "active" | "draft" | "archived";
type PlanStatus = "active" | "draft" | "inactive" | "archived";

interface AdhocMealPlan {
  id: number;
  planName: string;
  planCode: string;
  status: PlanStatus;
  planType: string;
  planSource: string;
  categoryName: string;
  clientName: string | null;
  createdById: number | null;
  createdOn: string;
}

const ITEMS_PER_PAGE = 10;

// Backend statuses come lowercase (draft | active | inactive | archived) —
// present them in title case, plus give inactive the same "In-Active" label
// used elsewhere in the product.
const statusLabel: Record<PlanStatus, string> = {
  active: "Active",
  draft: "Draft",
  inactive: "In-Active",
  archived: "Archived",
};

const statusBadgeClasses: Record<PlanStatus, string> = {
  active: "bg-[#EBF7F4] text-[#0DB5B5]",
  draft: "bg-[#F3F4F6] text-[#4B5563]",
  inactive: "bg-gray-100 text-gray-500",
  archived: "bg-gray-100 text-gray-500",
};

const formatCreatedOn = (iso: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

interface AssignClientOption {
  id: number;
  name: string;
  phone: string;
}

const AdhocMealPage: React.FC = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const [plans, setPlans] = useState<AdhocMealPlan[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Duplicate confirmation modal
  const [duplicatePlan, setDuplicatePlan] = useState<AdhocMealPlan | null>(null);

  // Delete confirmation modal (draft plans only)
  const [deletePlan, setDeletePlan] = useState<AdhocMealPlan | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Archive / restore reason modal target
  const [reasonTarget, setReasonTarget] = useState<{
    id: number;
    name: string;
    mode: ReasonModalMode;
  } | null>(null);

  // Bump to force the list effect to refetch after archive / restore.
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Assign-to-client modal
  const [assignPlan, setAssignPlan] = useState<AdhocMealPlan | null>(null);
  const [assignClientSearch, setAssignClientSearch] = useState("");
  const [assignClientOptions, setAssignClientOptions] = useState<AssignClientOption[]>([]);
  const [assignClientDropdownOpen, setAssignClientDropdownOpen] = useState(false);
  const [assignClientLoading, setAssignClientLoading] = useState(false);
  const [selectedAssignClient, setSelectedAssignClient] = useState<AssignClientOption | null>(null);
  const [assignClientGoal, setAssignClientGoal] = useState<string>("");
  const [assignClientSubscription, setAssignClientSubscription] = useState<string>("");
  const [assignProfileLoading, setAssignProfileLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const assignSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const assignClientDropdownRef = useRef<HTMLDivElement>(null);

  // ── Filter dropdown state (mirrors MealPlanTab.tsx) ─────────────────────
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterPlanTypes, setFilterPlanTypes] = useState<string[]>([]);
  const [selectedMealTypeFilters, setSelectedMealTypeFilters] = useState<string[]>([]);
  const [isFilterTypesLoading, setIsFilterTypesLoading] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filterDropdownPosition, setFilterDropdownPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });

  const toggleMealTypeFilter = (type: string) => {
    setSelectedMealTypeFilters((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
    setCurrentPage(1);
  };

  // Fetch plan types for filter (same API as MealPlanTab)
  useEffect(() => {
    const fetchFilterPlanTypes = async () => {
      setIsFilterTypesLoading(true);
      try {
        const data = await getMealPlanTypeList();
        if (data.success === "1" && Array.isArray(data.meal_plan_data)) {
          setFilterPlanTypes(
            data.meal_plan_data.map(
              (item: { meal_plan_type_name: string }) => item.meal_plan_type_name,
            ),
          );
        }
      } catch (err) {
        console.error("Failed to fetch filter plan types:", err);
      } finally {
        setIsFilterTypesLoading(false);
      }
    };
    fetchFilterPlanTypes();
  }, []);

  // Close the 3-dot menu on any scroll — it's portaled with position:fixed,
  // so scrolling the table body would otherwise leave it detached from its
  // trigger button.
  useEffect(() => {
    if (openMenuId === null) return;
    const handleScroll = () => setOpenMenuId(null);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [openMenuId]);

  // Close filter dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target as Node) &&
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };
    if (isFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFilterOpen]);

  // Debounce the search input so we don't hit the API on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;
    const fetchList = async () => {
      setLoading(true);
      setError(null);
      try {
        const status = activeTab === "all" ? "" : activeTab;
        const planTypeFilter = selectedMealTypeFilters.join(",");
        const api = await getAdhocMealPlanList(
          debouncedSearch,
          status,
          planTypeFilter,
          "",
          "",
          "",
          "",
          "",
          "",
          currentPage,
          ITEMS_PER_PAGE,
        );
        if (cancelled) return;
        if (api?.success) {
          const results = api.data?.results ?? [];
          const mapped: AdhocMealPlan[] = results.map((r: any) => ({
            id: r.adhoc_plan_id,
            planName: r.plan_name || "",
            planCode: r.plan_code || "",
            status: (r.status || "draft") as PlanStatus,
            planType: r.plan_type || "",
            planSource: r.plan_source || "",
            categoryName: r.category_name || "",
            clientName: r.client_name ?? null,
            createdById: r.created_by ?? null,
            createdOn: r.created_on || "",
          }));
          setPlans(mapped);
          setTotalCount(api.data?.pagination?.total_count ?? mapped.length);
        } else {
          setError(api?.message || "Failed to load adhoc meal plans");
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load adhoc meal plans");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchList();
    return () => {
      cancelled = true;
    };
  }, [activeTab, debouncedSearch, currentPage, selectedMealTypeFilters, refreshTrigger]);

  const goToCreate = () =>
    navigate("/adhoc-meal/create", { state: { adhocMealMode: true } });

  const goToView = (plan: AdhocMealPlan) =>
    navigate(`/adhoc-meal/${plan.id}`, {
      state: {
        adhocMealMode: true,
        mealPlan: {
          id: plan.id,
          plan_name: plan.planName,
          meal_type: plan.planType,
          created_by: plan.createdById,
          created_on: formatCreatedOn(plan.createdOn),
          status: statusLabel[plan.status],
        },
        mealTimings: [],
      },
    });

  // ── Assign-to-client modal handlers ─────────────────────────────────────
  const openAssignModal = (plan: AdhocMealPlan) => {
    setAssignPlan(plan);
    setAssignClientSearch("");
    setAssignClientOptions([]);
    setAssignClientDropdownOpen(false);
    setSelectedAssignClient(null);
    setAssignClientGoal("");
    setAssignClientSubscription("");
  };

  const closeAssignModal = () => {
    setAssignPlan(null);
    setAssignClientSearch("");
    setAssignClientOptions([]);
    setAssignClientDropdownOpen(false);
    setSelectedAssignClient(null);
    setAssignClientGoal("");
    setAssignClientSubscription("");
  };

  // Debounced client search while the assign modal is open
  useEffect(() => {
    if (!assignPlan) return;
    if (assignSearchDebounceRef.current) clearTimeout(assignSearchDebounceRef.current);
    setAssignClientLoading(true);
    assignSearchDebounceRef.current = setTimeout(async () => {
      try {
        const data = await getClientListForMealPlan(assignClientSearch.trim());
        if (data?.success && Array.isArray(data.data)) {
          setAssignClientOptions(
            (data.data as Array<{ user_id: number; name?: string; phone?: string }>).map((c) => ({
              id: c.user_id,
              name: c.name ?? "",
              phone: c.phone ?? "",
            })),
          );
        } else {
          setAssignClientOptions([]);
        }
      } catch {
        setAssignClientOptions([]);
      } finally {
        setAssignClientLoading(false);
      }
    }, 300);
    return () => {
      if (assignSearchDebounceRef.current) clearTimeout(assignSearchDebounceRef.current);
    };
  }, [assignClientSearch, assignPlan]);

  // Close the client dropdown on outside click
  useEffect(() => {
    if (!assignClientDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        assignClientDropdownRef.current &&
        !assignClientDropdownRef.current.contains(e.target as Node)
      ) {
        setAssignClientDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [assignClientDropdownOpen]);

  const handleSelectAssignClient = async (client: AssignClientOption) => {
    setSelectedAssignClient(client);
    setAssignClientSearch(client.name);
    setAssignClientDropdownOpen(false);
    setAssignClientGoal("");
    setAssignClientSubscription("");
    setAssignProfileLoading(true);
    try {
      const data = await getUserProfileDetails(client.id);
      if (data?.success === "1") {
        setAssignClientGoal(data.basic_information?.Goal || "");
        const subStatus = (data.subscription_details?.subscription_status || "").toLowerCase();
        if (subStatus === "expired") {
          setAssignClientSubscription("plan expired");
        } else {
          const subType = data.subscription_details?.subscription_type || "";
          setAssignClientSubscription(subType.replace(/\s*\([^)]*\)/g, "").trim());
        }
      }
    } catch {
      // Non-fatal: goal/subscription simply stay blank
    } finally {
      setAssignProfileLoading(false);
    }
  };

  const handleConfirmAssign = async () => {
    if (!assignPlan || !selectedAssignClient) return;
    setIsAssigning(true);
    try {
      const res = await assignAdhocMealPlanToClient({
        adhoc_plan_id: assignPlan.id,
        target_user_id: selectedAssignClient.id,
      });
      if (res?.success === "1" || res?.success === 1 || res?.success === true) {
        showSuccess(res.message || "Adhoc plan assigned to client successfully");
        closeAssignModal();
      } else {
        showError(res?.message || "Failed to assign adhoc plan to client");
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to assign adhoc plan to client");
    } finally {
      setIsAssigning(false);
    }
  };

  // Backend applies plan_type filtering, so `plans` already reflects the
  // active filter — no client-side pass needed.
  const filteredPlans = plans;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE)),
    [totalCount],
  );

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const tabs: { key: TabType; label: string }[] = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "draft", label: "Draft" },
    { key: "archived", label: "Archived" },
  ];

  return (
    <div
      className="h-screen flex flex-col bg-[#F7F7FA]"
      onClick={() => setOpenMenuId(null)}
    >
      <PageHeader title="Adhoc meal plans" />

      <div className="px-4 sm:px-6 lg:px-8 pb-4 flex-1 flex flex-col overflow-hidden">
        <div className="bg-white rounded-[6px] shadow-sm flex-1 flex flex-col border-[0.5px] border-[#F3F4F6] overflow-hidden">
          {/* Card Header */}
          <div className="p-4 sm:px-6 sm:py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 flex-shrink-0">
            <h3 className="text-lg sm:text-xl 3xl:text-[22px] font-semibold font-inter text-gray-900">
              Total adhoc meal plan ({totalCount})
            </h3>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <input
                  type="text"
                  placeholder="Search.."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-[260px] pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400 text-sm 3xl:text-[16px] font-inter"
                />
                <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
              <button
                ref={filterButtonRef}
                onClick={() => {
                  if (!isFilterOpen && filterButtonRef.current) {
                    const rect = filterButtonRef.current.getBoundingClientRect();
                    setFilterDropdownPosition({
                      top: rect.bottom + 6,
                      left: rect.right - 220,
                    });
                  }
                  setIsFilterOpen((prev) => !prev);
                }}
                className={`flex items-center gap-2 px-4 py-2 border rounded-md text-sm font-inter transition-colors ${
                  selectedMealTypeFilters.length > 0
                    ? "border-[#0DB5B5] text-[#0DB5B5] bg-[#F0FAFA]"
                    : "border-[#E0E0E0] text-[#1F1F1F] hover:border-[#0DB5B5]"
                }`}
              >
                <Filter className="w-4 h-4" />
                Filter
                {selectedMealTypeFilters.length > 0 && (
                  <span className="ml-1 bg-[#0DB5B5] text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none">
                    {selectedMealTypeFilters.length}
                  </span>
                )}
              </button>
              <Button
                variant="primary"
                startIcon={<Plus className="w-4 h-4" />}
                className="whitespace-nowrap"
                onClick={goToCreate}
              >
                Create
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-4 sm:px-6 flex gap-6 border-b border-gray-200 flex-shrink-0">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`pb-3 px-1 relative text-sm sm:text-base 3xl:text-[16px] font-medium font-inter transition-colors ${
                  activeTab === tab.key
                    ? "text-[#0DB5B5]"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
                {activeTab === tab.key && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0DB5B5]"></div>
                )}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Fixed Header */}
            <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] mx-5">
              <table className="w-full min-w-[1100px] table-fixed">
                <thead>
                  <tr className="bg-white">
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[3%]">
                      SI
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[22%]">
                      Plan name
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[12%]">
                      Plan Type
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[12%]">
                      Category
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[14%]">
                      Client
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[10%]">
                      Created By
                    </th>
                    <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm 3xl:text-[16px] font-inter font-normal w-[13%]">
                      Created On
                    </th>
                    <th className="w-[4%]" />
                  </tr>
                </thead>
              </table>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-x-auto overflow-y-auto mx-5 custom-scrollbar-overlay">
              {loading ? (
                <div className="flex items-center justify-center h-full py-16">
                  <div className="w-8 h-8 border-2 border-[#0DB5B5] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center h-full py-16 gap-3">
                  <p className="text-sm text-red-500">{error}</p>
                  <Button
                    variant="primary"
                    onClick={() => setCurrentPage((p) => p)}
                  >
                    Retry
                  </Button>
                </div>
              ) : filteredPlans.length === 0 ? (
                <div className="flex items-center justify-center h-full py-16">
                  <p className="text-gray-400 text-sm 3xl:text-base font-inter">
                    {debouncedSearch || selectedMealTypeFilters.length > 0
                      ? "No adhoc meal plans match your filters"
                      : "No adhoc meal plans yet"}
                  </p>
                </div>
              ) : (
                <table className="w-full min-w-[1100px] table-fixed">
                  <tbody>
                    {filteredPlans.map((plan, index) => {
                      const isLast = index === filteredPlans.length - 1;
                      const borderCls = isLast ? "" : "border-b border-[#111C2D1A]";
                      const displayIndex = String(
                        (currentPage - 1) * ITEMS_PER_PAGE + index + 1,
                      ).padStart(2, "0");
                      return (
                        <tr
                          key={plan.id}
                          onClick={() => goToView(plan)}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          {/* SI */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm 3xl:text-base font-inter w-[3%] ${borderCls}`}>
                            {displayIndex}
                          </td>

                          {/* Plan name + code + status pill */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 w-[22%] ${borderCls}`}>
                            <div className="flex flex-col min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <span
                                  title={plan.planName}
                                  className="font-medium font-inter text-[#1F1F1F] text-sm 3xl:text-base truncate"
                                >
                                  {plan.planName}
                                </span>
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] 3xl:text-[13px] font-medium font-inter flex-shrink-0 ${statusBadgeClasses[plan.status]}`}
                                >
                                  {statusLabel[plan.status]}
                                </span>
                              </div>
                              <span className="text-[#646464] text-xs 3xl:text-[13px] font-inter truncate">
                                {plan.planCode}
                              </span>
                            </div>
                          </td>

                          {/* Plan Type */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm 3xl:text-base font-inter w-[12%] ${borderCls}`}>
                            {plan.planType || "-"}
                          </td>

                          {/* Category */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm 3xl:text-base font-inter w-[12%] ${borderCls}`}>
                            {plan.categoryName || "-"}
                          </td>

                          {/* Client */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm 3xl:text-base font-inter w-[14%] ${borderCls}`}>
                            {plan.clientName || "-"}
                          </td>

                          {/* Created By */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm 3xl:text-base font-inter w-[10%] ${borderCls}`}>
                            {plan.createdById ?? "-"}
                          </td>

                          {/* Created On */}
                          <td className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm 3xl:text-base font-inter w-[13%] ${borderCls}`}>
                            {formatCreatedOn(plan.createdOn)}
                          </td>

                          {/* Three-dot menu */}
                          <td className={`py-3 sm:py-4 pr-4 w-[4%] ${borderCls}`}>
                            <div
                              className="relative"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={(e) => {
                                  if (openMenuId === plan.id) {
                                    setOpenMenuId(null);
                                    return;
                                  }
                                  // Estimate menu height by item count so the
                                  // flip decision reflects what will actually
                                  // render (draft=2, archived=1, else=3 items,
                                  // ~40px per item + container padding).
                                  const itemCount =
                                    plan.status === "draft"
                                      ? 2
                                      : plan.status === "archived"
                                        ? 1
                                        : 3;
                                  const estimatedMenuHeight = itemCount * 40 + 16;
                                  const MENU_WIDTH = 176; // matches w-44
                                  const rect = (
                                    e.currentTarget as HTMLElement
                                  ).getBoundingClientRect();
                                  const spaceBelow =
                                    window.innerHeight - rect.bottom;
                                  const openUp = spaceBelow < estimatedMenuHeight;
                                  setMenuPosition({
                                    top: openUp
                                      ? rect.top - estimatedMenuHeight - 4
                                      : rect.bottom + 4,
                                    left: rect.right - MENU_WIDTH,
                                  });
                                  setOpenMenuId(plan.id);
                                }}
                                className="p-1 rounded hover:bg-gray-100 transition-colors"
                              >
                                <MoreVertical className="w-4 h-4 text-gray-500" />
                              </button>
                              {openMenuId === plan.id && ReactDOM.createPortal(
                                <div
                                  style={{
                                    position: "fixed",
                                    top: menuPosition.top,
                                    left: menuPosition.left,
                                    zIndex: 1000,
                                    width: "176px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="bg-white border border-gray-200 rounded-lg shadow-lg py-1"
                                >
                                  {plan.status === "draft" ? (
                                    <>
                                      <button
                                        onClick={() => { setOpenMenuId(null); goToView(plan); }}
                                        className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] hover:bg-gray-50 transition-colors"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => {
                                          setOpenMenuId(null);
                                          setDeletePlan(plan);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#FF3B30] hover:bg-[#FFF5F5] transition-colors"
                                      >
                                        Delete
                                      </button>
                                    </>
                                  ) : plan.status === "archived" ? (
                                    <button
                                      onClick={() => {
                                        setOpenMenuId(null);
                                        setReasonTarget({
                                          id: plan.id,
                                          name: plan.planName,
                                          mode: "restore",
                                        });
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] hover:bg-gray-50 transition-colors"
                                    >
                                      Restore
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => { setOpenMenuId(null); openAssignModal(plan); }}
                                        className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] hover:bg-gray-50 transition-colors"
                                      >
                                        Assign to client
                                      </button>
                                      <button
                                        onClick={() => { setOpenMenuId(null); setDuplicatePlan(plan); }}
                                        className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#1F1F1F] hover:bg-gray-50 transition-colors"
                                      >
                                        Duplicate
                                      </button>
                                      <button
                                        onClick={() => {
                                          setOpenMenuId(null);
                                          setReasonTarget({
                                            id: plan.id,
                                            name: plan.planName,
                                            mode: "archive",
                                          });
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm 3xl:text-[16px] font-inter text-[#FF3B30] hover:bg-[#FFF5F5] transition-colors"
                                      >
                                        Archive
                                      </button>
                                    </>
                                  )}
                                </div>,
                                document.body,
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            resultCount={filteredPlans.length}
          />
        </div>
      </div>

      {/* Assign-to-client modal */}
      {assignPlan && (
        <div
          className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
          onClick={(e) => {
            e.stopPropagation();
            if (!isAssigning) closeAssignModal();
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg 3xl:text-xl font-semibold font-inter text-[#1F1F1F] mb-3">
              Assign plan to a client
            </h2>
            <div className="border-t border-[#F0F0F0] mb-6" />

            <div className="flex items-center gap-8 mb-6">
              {/* Client search dropdown */}
              <div className="relative w-[45%]" ref={assignClientDropdownRef}>
                <div
                  onClick={() => setAssignClientDropdownOpen((v) => !v)}
                  className="flex items-center justify-between px-4 py-3 border border-[#E0E0E0] rounded-lg cursor-pointer hover:border-[#0DB5B5] transition-colors"
                >
                  <input
                    type="text"
                    placeholder="Search client"
                    value={assignClientSearch}
                    onFocus={() => setAssignClientDropdownOpen(true)}
                    onChange={(e) => {
                      setAssignClientSearch(e.target.value);
                      setAssignClientDropdownOpen(true);
                      if (selectedAssignClient) {
                        setSelectedAssignClient(null);
                        setAssignClientGoal("");
                        setAssignClientSubscription("");
                      }
                    }}
                    className="flex-1 min-w-0 text-sm font-inter text-[#1F1F1F] placeholder:text-[#8E8E93] bg-transparent focus:outline-none"
                  />
                  <ChevronDown
                    className={`w-4 h-4 text-[#8E8E93] flex-shrink-0 ml-2 transition-transform ${
                      assignClientDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </div>
                {assignClientDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E0E0E0] rounded-lg shadow-lg max-h-60 overflow-y-auto z-10 custom-scrollbar-overlay">
                    {assignClientLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="w-4 h-4 border-2 border-[#0DB5B5] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : assignClientOptions.length === 0 ? (
                      <p className="px-3 py-3 text-sm text-[#8E8E93] font-inter">
                        No clients found
                      </p>
                    ) : (
                      assignClientOptions.map((client) => (
                        <div
                          key={client.id}
                          onClick={() => handleSelectAssignClient(client)}
                          className="px-3 py-2 cursor-pointer hover:bg-[#F9FEFE] transition-colors"
                        >
                          <div className="text-sm font-inter text-[#1F1F1F]">{client.name}</div>
                          {client.phone && (
                            <div className="text-xs font-inter text-[#8E8E93]">{client.phone}</div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Goal display */}
              <div className="flex flex-col justify-center min-w-0 flex-1">
                <span className="text-sm font-inter text-[#8E8E93] mb-1">Goal</span>
                <span className="text-sm font-inter text-[#1F1F1F] truncate">
                  {assignProfileLoading ? "…" : assignClientGoal || "-"}
                </span>
              </div>

              {/* Subscription plan display */}
              <div className="flex flex-col justify-center min-w-0 flex-1">
                <span className="text-sm font-inter text-[#8E8E93] mb-1">Subscription plan</span>
                <span className="text-sm font-inter text-[#1F1F1F] truncate">
                  {assignProfileLoading ? "…" : assignClientSubscription || "-"}
                </span>
              </div>
            </div>

            <div className="border-t border-[#F0F0F0] mb-4" />
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                size="md"
                onClick={closeAssignModal}
                disabled={isAssigning}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleConfirmAssign}
                loading={isAssigning}
                disabled={!selectedAssignClient || isAssigning}
              >
                Assign plan
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate confirmation modal */}
      {duplicatePlan && (
        <div
          className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
          onClick={(e) => {
            e.stopPropagation();
            setDuplicatePlan(null);
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg 3xl:text-xl font-semibold font-inter text-[#1F1F1F] mb-3">
              Duplicate adhoc meal plan confirmation?
            </h2>
            <div className="border-t border-[#F0F0F0] mb-4" />
            <p className="text-sm 3xl:text-base font-inter text-[#1F1F1F] mb-6 leading-relaxed">
              Are you sure you want to duplicate this meal plan? All existing meals and details will be copied to a new plan.
            </p>
            <div className="border-t border-[#F0F0F0] mb-4" />
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => setDuplicatePlan(null)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => {
                  const sourcePlan = duplicatePlan;
                  setDuplicatePlan(null);
                  navigate("/adhoc-meal/create", {
                    state: {
                      adhocMealMode: true,
                      duplicateMode: true,
                      sourcePlanId: sourcePlan.id,
                      sourcePlanName: sourcePlan.planName,
                    },
                  });
                }}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal (draft plans) */}
      {deletePlan && (
        <div
          className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
          onClick={(e) => {
            e.stopPropagation();
            if (!isDeleting) setDeletePlan(null);
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg 3xl:text-xl font-semibold font-inter text-[#1F1F1F] mb-3">
              Delete adhoc plan
            </h2>
            <div className="border-t border-[#F0F0F0] mb-4" />
            <p className="text-sm 3xl:text-base font-inter text-[#1F1F1F] mb-6 leading-relaxed">
              Are you sure you want to delete &ldquo;{deletePlan.planName}&rdquo; meal plan? This action cannot be undone.
            </p>
            <div className="border-t border-[#F0F0F0] mb-4" />
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => setDeletePlan(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="md"
                loading={isDeleting}
                disabled={isDeleting}
                onClick={async () => {
                  if (!deletePlan) return;
                  setIsDeleting(true);
                  try {
                    const api = await deleteAdhocMealPlan(deletePlan.id);
                    const isSuccess =
                      api?.success === true ||
                      api?.success === "1" ||
                      api?.success === 1 ||
                      api?.success === "true" ||
                      api?.data?.deleted === true;
                    if (!isSuccess) {
                      throw new Error(api?.message || "Failed to delete adhoc plan");
                    }
                    showSuccess(api?.message || "Draft plan deleted successfully");
                    setDeletePlan(null);
                    setRefreshTrigger((t) => t + 1);
                  } catch (err) {
                    showError(
                      err instanceof Error ? err.message : "Failed to delete adhoc plan",
                    );
                  } finally {
                    setIsDeleting(false);
                  }
                }}
              >
                Yes
              </Button>
            </div>
          </div>
        </div>
      )}

      {isFilterOpen &&
        ReactDOM.createPortal(
          <div
            ref={filterDropdownRef}
            style={{
              position: "fixed",
              top: `${filterDropdownPosition.top}px`,
              left: `${filterDropdownPosition.left}px`,
              zIndex: 1000,
              width: "220px",
            }}
            className="bg-white border border-[#E0E0E0] rounded-lg shadow-lg overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#F3F4F6]">
              <span className="text-sm font-semibold font-inter text-[#1F1F1F]">
                Meal type
              </span>
              <div className="flex items-center gap-3">
                {selectedMealTypeFilters.length > 0 && (
                  <button
                    onClick={() => {
                      setSelectedMealTypeFilters([]);
                      setCurrentPage(1);
                    }}
                    className="text-xs text-[#0DB5B5] hover:text-[#0CA0A0] font-inter font-medium transition-colors"
                  >
                    Clear all
                  </button>
                )}
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="text-[#8E8E93] hover:text-[#1F1F1F] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div
              className="overflow-y-auto custom-scrollbar-overlay"
              style={{ maxHeight: "280px" }}
            >
              {isFilterTypesLoading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="w-5 h-5 border-2 border-[#0DB5B5] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filterPlanTypes.length === 0 ? (
                <p className="px-4 py-4 text-sm text-[#8E8E93] font-inter">
                  No plan types available
                </p>
              ) : (
                filterPlanTypes.map((type) => {
                  const isChecked = selectedMealTypeFilters.includes(type);
                  return (
                    <div
                      key={type}
                      onClick={() => toggleMealTypeFilter(type)}
                      className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-[#F9FEFE] transition-colors select-none"
                    >
                      <div
                        className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center transition-colors ${
                          isChecked
                            ? "bg-[#0DB5B5] border-[#0DB5B5]"
                            : "bg-white border-[#C7C7CC]"
                        }`}
                      >
                        {isChecked && (
                          <svg
                            className="w-2.5 h-2.5 text-white"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={3}
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                      <span
                        className={`text-sm font-inter ${
                          isChecked
                            ? "text-[#1F1F1F] font-medium"
                            : "text-[#3C3C43]"
                        }`}
                      >
                        {type}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}

      <IngredientReasonModal
        open={reasonTarget !== null}
        mode={reasonTarget?.mode ?? "archive"}
        itemLabel="AdHoc plan"
        ingredientName={reasonTarget?.name}
        onClose={() => setReasonTarget(null)}
        onConfirm={async (reason) => {
          const target = reasonTarget;
          if (!target) return;
          const isRestore = target.mode === "restore";
          try {
            const api = isRestore
              ? await restoreAdhocMealPlan(target.id, reason)
              : await archiveAdhocMealPlan(target.id, reason);
            const isSuccess =
              api?.success === true ||
              api?.success === "1" ||
              api?.success === 1 ||
              api?.success === "true";
            if (!isSuccess) {
              throw new Error(
                api?.message ||
                  (isRestore
                    ? "Failed to restore adhoc plan"
                    : "Failed to archive adhoc plan"),
              );
            }
            setReasonTarget(null);
            showSuccess(
              api?.message ||
                (isRestore
                  ? "Plan restored successfully"
                  : "Plan archived successfully"),
            );
            setRefreshTrigger((t) => t + 1);
          } catch (err) {
            setReasonTarget(null);
            showError(
              err instanceof Error
                ? err.message
                : isRestore
                  ? "Failed to restore adhoc plan"
                  : "Failed to archive adhoc plan",
            );
          }
        }}
      />

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default AdhocMealPage;
